// public/v3/src/components/role-edit-modal.jsx — create/edit a role.
// Name + base tier + feature checkboxes (page-level). Built-in (locked) roles
// keep Name + Base tier read-only but allow editing the feature set.
// Uses the shared unsaved-changes guard.
import React from 'react';
import { useUnsavedChangesGuard } from '../lib/hooks/use-unsaved-changes-guard';
import { ALL_FEATURES, FEATURE_LABELS, featuresAllowedForTier } from '../lib/feature-catalog';

const TIERS = ['staff', 'provider', 'admin'];

export function RoleEditModal({ mode, role, onClose, onSubmit }) {
  const lockIdentity = !!role?.isLocked;
  const [name, setName] = React.useState(role?.name || '');
  const [baseTier, setBaseTier] = React.useState(role?.baseTier || 'staff');
  const [features, setFeatures] = React.useState(role?.features || []);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const { attemptDismiss, showDiscardPrompt, confirmDiscard, keepEditing } =
    useUnsavedChangesGuard({ values: { name, baseTier, features: features.join(',') }, onClose, busy });

  const allowed = featuresAllowedForTier(baseTier);
  React.useEffect(() => { setFeatures((prev) => prev.filter((f) => allowed.includes(f))); /* eslint-disable-next-line */ }, [baseTier]);

  function toggle(f) {
    setFeatures((prev) => prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]);
  }
  async function submit() {
    setError('');
    if (!name.trim()) { setError('Name is required.'); return; }
    setBusy(true);
    try { await onSubmit({ name: name.trim(), baseTier, features }); onClose(); }
    catch (e) { setError(e?.message || 'Save failed.'); setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={attemptDismiss}>
      <div className="modal usermgmt-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{lockIdentity ? `Role: ${role.name} (built-in)` : (mode === 'add' ? 'New role' : 'Edit role')}</h3>
        </div>
        {showDiscardPrompt ? (
          <>
            <div className="modal-body">
              <h3 style={{ marginTop: 0 }}>Discard changes?</h3>
              <p>You've made changes but haven't saved yet.</p>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={keepEditing}>Keep editing</button>
              <button className="btn warn" onClick={confirmDiscard}>Discard</button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-body">
              <div className="field">
                <div className="field-label">Name</div>
                <input
                  type="text"
                  value={name}
                  disabled={lockIdentity || busy}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus={!lockIdentity}
                />
              </div>
              <div className="field">
                <div className="field-label">Base tier</div>
                <select value={baseTier} disabled={lockIdentity || busy} onChange={(e) => setBaseTier(e.target.value)}>
                  {TIERS.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <fieldset className="usermgmt-features">
                <legend>Feature access</legend>
                {ALL_FEATURES.map((f) => {
                  const tierAllows = allowed.includes(f);
                  return (
                    <label key={f} className={tierAllows ? '' : 'feat-disabled'}>
                      <input
                        type="checkbox"
                        disabled={baseTier === 'admin' || !tierAllows || busy}
                        checked={baseTier === 'admin' ? true : features.includes(f)}
                        onChange={() => toggle(f)}
                      />
                      {FEATURE_LABELS[f]}
                    </label>
                  );
                })}
                {baseTier === 'admin' && <p className="usermgmt-hint">Administrators have all features.</p>}
              </fieldset>
              {error && <div className="usermgmt-error" role="alert">{error}</div>}
            </div>
            <div className="modal-foot">
              <span />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={attemptDismiss} disabled={busy}>
                  Cancel
                </button>
                <button className="btn primary" onClick={submit} disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
